<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">CPD Activity Category</div>
          <div class="lookup-subtitle">Manage Category 2 (CPD Activity) categories.</div>
        </div>

        <v-text-field
          v-model="search"
          class="lookup-search"
          append-icon="mdi-magnify"
          hide-details
          label="Search"
          single-line
        ></v-text-field>

        <v-btn
          v-if="canCreateCategory"
          class="lookup-add-button"
          depressed
          @click="addCategory()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Category</span>
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="categories.rows"
        :loading="loading"
        :options.sync="options"
        :server-items-length="categories.count"
        class="lookup-table elevation-1"
        loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-if="canEditCategory" v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon class="mr-2 lookup-action-icon" @click="editCategory(item)" v-on="on">
                mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>{{ title }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="50%">
        <v-card class="lookup-dialog-card">
          <v-card-title class="lookup-dialog__title" primary-title>
            {{ title }}
            <v-spacer></v-spacer>
            <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <AddCpdActivityCategory
            v-if="dialog"
            :category="category"
            @close="closeDialog()"
            @registerOrUpdateCategory="registerOrUpdateCategory"
          />
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { getUser } from "@/utils/helper";
import { LookupService } from "../../../common/api.service";
import AddCpdActivityCategory from "./add-cpd-activity-category.vue";
import { CpdActivityCategoryService } from "./cpdActivityCategory.service";

export default {
  components: {
    AddCpdActivityCategory,
  },
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      headers: [
        { text: "Category Name", value: "name" },
        { text: "Category Code", value: "code" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      categories: { rows: [], count: 0 },
      category: null,
      dialog: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      title: "",
      categoryType: "CAT2",
    };
  },
  computed: {
    canCreateCategory() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "CPD Activity Category" &&
          permission.canCreate
      );
    },
    canEditCategory() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "CPD Activity Category" &&
          permission.canUpdate
      );
    },
  },
  async beforeMount() {
    this.fetchCategories();
  },
  watch: {
    options: {
      handler() {
        this.fetchCategories();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchCategories();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchCategories();
      }, 400);
    },
  },
  methods: {
    async fetchCategories() {
      this.loading = true;
      const { page, itemsPerPage } = this.options;
      const res = await LookupService.getCategoriesWP(
        page,
        itemsPerPage,
        this.search,
        this.categoryType
      );
      if (res.status === 200) {
        this.categories = res.data;
      } else {
        this.$toast.error("There was a problem fetching categories, Please try Again!");
      }
      this.loading = false;
    },
    addCategory() {
      this.category = null;
      this.dialog = true;
      this.title = "Add CPD Activity Category";
    },
    editCategory(item) {
      this.category = item;
      this.dialog = true;
      this.title = "Edit CPD Activity Category";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchCategories();
    },
    async registerOrUpdateCategory(category) {
      this.loading = true;
      let res = null;
      let shouldClose = false;

      if (category.editMode) {
        res = await CpdActivityCategoryService.update(category.id, category);
        if (res?.data?.status == 200 || res?.status === 200 || res?.status === 201) {
          this.$toast.success(res.data.message);
          shouldClose = true;
        } else if (res?.data?.status == 500) {
          this.$toast.error(res.data.message);
        } else {
          this.$toast.error("Unable to update category, please try again");
        }
      } else {
        res = await CpdActivityCategoryService.create(category);
        if (res?.data?.status == 200 || res?.status === 200 || res?.status === 201) {
          this.$toast.success(res.data.message);
          shouldClose = true;
        } else if (res?.data?.status == 500) {
          this.$toast.error(res.data.message);
        } else {
          this.$toast.error("Unable to create category, please try again");
        }
      }
      this.loading = false;
      if (shouldClose) {
        this.closeDialog();
      }
    },
  },
};
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.lookup-table {
  border-radius: 0 0 12px 12px;
}

.lookup-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.lookup-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.lookup-dialog__close {
  color: #ffffff !important;
}

.lookup-dialog__close .v-icon {
  color: #ffffff !important;
}

.lookup-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-action-icon {
  color: #1976d2 !important;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .lookup-search {
    max-width: 100%;
  }

  .lookup-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
